import {
  ICopyDefaultToContractBody,
  IDefaultRulePropagationApplyRequest,
  IDefaultRulePropagationApplyResponse,
  IDefaultRulePropagationPreviewResponse,
  IRulesetListParams,
} from "types/DefaultRules/DefaultRules";
import {
  ILesserOfChargesRuleset,
  ILesserOfChargesRulesetCreateRequest,
  ILesserOfChargesRulesetUpdateRequest,
} from "types/LesserOfCharges/LesserOfCharges";

import { api } from ".";

interface IListResponse {
  data: ILesserOfChargesRuleset[];
  message?: string;
}

interface IItemResponse {
  data: ILesserOfChargesRuleset;
  message?: string;
}

const lesserOfChargesQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllLesserOfCharges: builder.query<
      IListResponse,
      IRulesetListParams | null | void
    >({
      query: (params) => {
        let search = "";
        if (params?.applicability?.length) {
          search = `?applicability=${params.applicability.join(",")}`;
        } else if (params && params.isDefault !== undefined) {
          search = `?isDefault=${params.isDefault}`;
        }
        return `lesser-of-charges/all${search}`;
      },
      providesTags: ["LesserOfChargesRulesets"],
    }),
    getLesserOfChargesById: builder.query<IItemResponse, string>({
      query: (id) => `lesser-of-charges/${id}`,
      providesTags: (_r, _e, id) => [{ type: "LesserOfChargesRulesets", id }],
    }),
    createLesserOfCharges: builder.mutation<
      IItemResponse,
      ILesserOfChargesRulesetCreateRequest
    >({
      query: (body) => ({
        url: "lesser-of-charges",
        method: "POST",
        body,
      }),
      invalidatesTags: ["LesserOfChargesRulesets"],
    }),
    updateLesserOfCharges: builder.mutation<
      IItemResponse,
      ILesserOfChargesRulesetUpdateRequest
    >({
      query: (body) => ({
        url: `lesser-of-charges/${body.id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["LesserOfChargesRulesets", "DefaultRulePropagation"],
    }),
    deleteLesserOfCharges: builder.mutation<void, string>({
      query: (id) => ({
        url: `lesser-of-charges/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["LesserOfChargesRulesets", "DefaultRulePropagation"],
    }),
    cloneLesserOfChargesAsDefault: builder.mutation<IItemResponse, string>({
      query: (id) => ({
        url: `lesser-of-charges/${id}/clone-as-default`,
        method: "POST",
      }),
      invalidatesTags: ["LesserOfChargesRulesets"],
    }),
    copyLesserOfChargesToContract: builder.mutation<
      IItemResponse,
      { id: string; body: ICopyDefaultToContractBody }
    >({
      query: ({ id, body }) => ({
        url: `lesser-of-charges/${id}/copy-to-contract`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["LesserOfChargesRulesets", "ContractFeeSchedules"],
    }),
    getLesserOfChargesPropagationPreview: builder.query<
      IDefaultRulePropagationPreviewResponse,
      string
    >({
      query: (id) => `lesser-of-charges/${id}/propagate/preview`,
      providesTags: (_r, _e, id) => [
        { type: "DefaultRulePropagation", id: `lesserOfCharges:${id}` },
      ],
    }),
    applyLesserOfChargesPropagation: builder.mutation<
      IDefaultRulePropagationApplyResponse,
      { id: string; requests: IDefaultRulePropagationApplyRequest[] }
    >({
      query: ({ id, requests }) => ({
        url: `lesser-of-charges/${id}/propagate`,
        method: "POST",
        body: { requests },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "LesserOfChargesRulesets",
        { type: "DefaultRulePropagation", id: `lesserOfCharges:${id}` },
      ],
    }),
  }),
});

export const {
  useGetAllLesserOfChargesQuery,
  useGetLesserOfChargesByIdQuery,
  useCreateLesserOfChargesMutation,
  useUpdateLesserOfChargesMutation,
  useDeleteLesserOfChargesMutation,
  useCloneLesserOfChargesAsDefaultMutation,
  useCopyLesserOfChargesToContractMutation,
  useGetLesserOfChargesPropagationPreviewQuery,
  useLazyGetLesserOfChargesPropagationPreviewQuery,
  useApplyLesserOfChargesPropagationMutation,
} = lesserOfChargesQueries;
